import { DownOutlined } from "@ant-design/icons";
import { Select } from "antd";
import { useAntdMessage } from "hooks/useAntdMessage";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { websiteIdsSelector } from "appredux/selectors";
import { ONLY_FOR_CRMKLEDO } from "utils/static";
import { cn } from "utils/helper";
import { useMSScheduleDetail } from "queries/meetingSchedule";
import { SCHEDULE_STATUS } from "../../scheduleStatus";
import MeetingReportModal from "../../Finished/MeetingReportModal";
import MeetingReportWhatsappModal from "../../Finished/MeetingReportWhatsappModal";
import {
  resolveScheduleAdminGroupId,
  resolveScheduleContactId,
} from "../../Schedule/scheduleDetailResolvers";
import { resolveAdminGroupAppId } from "../../meetingAdminApp";
import {
  formatStatusLabel,
  getScheduleRowStatusId,
  getStatusMeta,
} from "./scheduleRowFields";

const { Option } = Select;

/** Teks hitam (opsi + trigger via CSS var) */
const SCHEDULE_STATUS_TEXT = "#000";

const ScheduleStatusSelect = ({
  record,
  statuses,
  locale,
  patchStatusMutation,
  onStatusPatchSuccess,
  selectClassName = "min-w-[190px] !text-black",
}) => {
  const { t } = useTranslation();
  const { message } = useAntdMessage();
  const websiteId = useSelector(websiteIdsSelector);
  const isInternal = ONLY_FOR_CRMKLEDO.includes(websiteId);
  const statusId = getScheduleRowStatusId(record);
  const meta = useMemo(
    () => getStatusMeta(statuses, statusId),
    [statuses, statusId]
  );

  const [reportOpen, setReportOpen] = useState(false);
  // Modal pratinjau WhatsApp yang muncul setelah report tersimpan.
  const [waOpen, setWaOpen] = useState(false);
  const [waReport, setWaReport] = useState("");
  const [waAppId, setWaAppId] = useState(null);

  // Row tabel sering hanya berisi nama kontak (tanpa contact_id/admin_group_id).
  // Ambil detail lengkap saat modal report dibuka agar rekomendasi & default app
  // benar; fallback ke field row bila ada.
  const { data: detail, isLoading: detailLoading } = useMSScheduleDetail(
    record.id,
    { enabled: isInternal && reportOpen && record.id != null }
  );

  const contactId = useMemo(
    () => resolveScheduleContactId(detail) ?? resolveScheduleContactId(record),
    [detail, record]
  );
  // Kontak masih "pending" bila detail sedang dimuat & row belum punya contact_id.
  const contactLoading =
    detailLoading && resolveScheduleContactId(record) == null;
  const defaultAppId = useMemo(
    () =>
      resolveAdminGroupAppId(
        resolveScheduleAdminGroupId(detail) ??
          resolveScheduleAdminGroupId(record)
      ),
    [detail, record]
  );

  const isRowSaving =
    patchStatusMutation.isPending &&
    patchStatusMutation.variables?.id === record.id;

  const runStatusPatch = useCallback(
    (newId, extra, onDone) => {
      patchStatusMutation.mutate(
        {
          id: record.id,
          status_id: Number(newId),
          ...(extra || {}),
        },
        {
          onSuccess: () => {
            message.success(t("meeting_management.status_updated"));
            // Hanya status_id + report yang relevan untuk update row tampilan;
            // company_id/app_id tidak perlu nempel ke record list.
            onStatusPatchSuccess?.({
              status_id: Number(newId),
              ...(extra?.report != null ? { report: extra.report } : {}),
            });
            onDone?.();
          },
          onError: (err) => {
            message.error(
              err?.response?.data?.message ||
                t("meeting_management.status_update_failed")
            );
          },
        }
      );
    },
    [message, onStatusPatchSuccess, patchStatusMutation, record.id, t]
  );

  const onSelectStatus = useCallback(
    (newId) => {
      if (Number(newId) === Number(statusId)) {
        return;
      }
      // Ubah ke Selesai dari status lain → wajib isi report dulu.
      if (
        Number(newId) === SCHEDULE_STATUS.DONE &&
        Number(statusId) !== SCHEDULE_STATUS.DONE
      ) {
        setReportOpen(true);
        return;
      }
      runStatusPatch(newId);
    },
    [runStatusPatch, statusId]
  );

  // payload = { report, company_id?, app_id? } dari MeetingReportModal.
  const handleReportSubmit = useCallback(
    (payload, appId) => {
      runStatusPatch(SCHEDULE_STATUS.DONE, payload, () => {
        setReportOpen(false);
        // Lanjut ke pratinjau WhatsApp; pakai report yang baru disubmit sebagai
        // report_override agar ringkasan AI mengikuti teks terkini.
        setWaReport(payload.report);
        setWaAppId(appId);
        setWaOpen(true);
      });
    },
    [runStatusPatch]
  );

  const hasTriggerColor = Boolean(meta?.color);
  const cellStyle = {
    "--schedule-status-sel-fg": SCHEDULE_STATUS_TEXT,
    ...(hasTriggerColor && meta.color
      ? { "--schedule-status-sel-bg": meta.color }
      : {}),
  };

  return (
    <>
    <div
      className={cn(
        "schedule-status-select-cell inline-block max-w-full align-middle"
      )}
      data-has-color={hasTriggerColor ? "true" : "false"}
      style={cellStyle}
    >
      <Select
        variant={hasTriggerColor ? "borderless" : "outlined"}
        className={selectClassName}
        popupClassName="ms-schedule-status-select-dropdown"
        styles={{
          popup: {
            root: {
              padding: 0,
            },
          },
        }}
        value={statusId != null ? statusId : undefined}
        placeholder="—"
        disabled={!statuses?.length}
        loading={isRowSaving}
        suffixIcon={<DownOutlined className="!text-black" />}
        onChange={onSelectStatus}
      >
        {(statuses || []).map((s) => {
          const hasColor = Boolean(s.color);
          const bg = hasColor ? s.color : "#f0f0f0";
          return (
            <Option
              key={s.id}
              value={s.id}
              disabled={Number(s.id) === Number(statusId)}
              style={{
                backgroundColor: bg,
                color: SCHEDULE_STATUS_TEXT,
              }}
              className="ms-schedule-status-option rounded-none font-medium !text-black"
            >
              {formatStatusLabel(s, locale)}
            </Option>
          );
        })}
      </Select>
    </div>
    <MeetingReportModal
      open={reportOpen}
      defaultReport={record.report ?? ""}
      loading={isRowSaving}
      onSubmit={handleReportSubmit}
      onCancel={() => setReportOpen(false)}
      isInternal={isInternal}
      contactId={contactId}
      contactLoading={contactLoading}
      defaultAppId={defaultAppId}
    />
    <MeetingReportWhatsappModal
      open={waOpen}
      scheduleId={record.id}
      reportOverride={waReport}
      appId={waAppId}
      onClose={() => setWaOpen(false)}
    />
    </>
  );
};

export default ScheduleStatusSelect;
